import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  DialogContent,
  DialogTitle,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';
import EventManager, { type EventType } from '../../components/EventManager';
import { documentsService } from '../../modules/docs/services/documents.service';
import { emailActivity, type EmailActivityLog } from '../../lib/emailActivityService';
import type { Customer } from '../../services/customerService';
import type { Project } from '../../types/project';
import type { WorkflowTask } from '../../components/ProjectWorkflow';
import { directSupabase } from '../../lib/supabaseClient';
import ProjectLifecycleBar from '../../components/ProjectLifecycleBar';
import WorkspaceCalendarEventsCard from './WorkspaceCalendarEventsCard';
import WorkspaceCommentsCard from './WorkspaceCommentsCard';
import JobCardCommentsCard from './JobCardCommentsCard';
import WorkspaceLifecycleRefCard from './WorkspaceLifecycleRefCard';
import WorkspaceProductTypesCard from './WorkspaceProductTypesCard';
import WorkspaceRecentActivityCard from './WorkspaceRecentActivityCard';
import type { WorkspaceSnapshot } from './workspaceDashboard.model';
import type { OperationalQueueId } from './operationalState';

interface InitiationStagePanelProps {
  project: Project;
  customer: Customer | null;
  tasks: WorkflowTask[];
  onProjectPatch: (patch: Partial<Project>) => Promise<void>;
  onTaskChange: (taskName: string, completed: boolean) => void;
  saving?: boolean;
  calendarSyncKey?: number;
  onLinkedTaskChange?: (taskName: string, completed: boolean) => Promise<void>;
  /** Where the job is, from the same records the Board uses. */
  snapshot?: WorkspaceSnapshot | null;
}

type TimelineKind = 'send' | 'accept' | 'reject' | 'query';

type CorrespondenceRow = {
  id: string;
  kind: TimelineKind;
  subject: string;
  title?: string;
  recipient: string;
  status: string;
  template_type: string;
  created_at: string;
  metadata?: unknown;
  previewId?: string | null;
  openCount?: number;
  openedAt?: string;
  relatedLabel?: string;
  detail?: string;
  version?: number | null;
};

function asMeta(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function asAction(value: unknown): { action?: string; at?: string; reason?: string; message?: string } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const row = value as Record<string, unknown>;
  return {
    action: typeof row.action === 'string' ? row.action : undefined,
    at: typeof row.at === 'string' ? row.at : undefined,
    reason: typeof row.reason === 'string' ? row.reason : undefined,
    message: typeof row.message === 'string' ? row.message : undefined,
  };
}

function formatStamp(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-GB');
}

function documentNoun(row: CorrespondenceRow) {
  const subject = String(row.subject || '').toLowerCase();
  if (subject.includes('invoice')) return 'invoice';
  if (subject.includes('quotation') || subject.includes('quote')) return 'quote';
  if (subject.includes('estimate')) return 'estimate';
  const meta = asMeta(row.metadata);
  const kind = String(meta.documentKind || meta.document_kind || meta.kind || '').toLowerCase();
  if (kind.includes('invoice')) return 'invoice';
  if (kind.includes('quote')) return 'quote';
  if (kind.includes('estimate')) return 'estimate';
  const template = String(row.template_type || '').toLowerCase();
  if (template.includes('invoice')) return 'invoice';
  if (template.includes('estimate')) return 'estimate';
  if (template.includes('quote')) return 'quote';
  if (template.includes('deposit')) return 'deposit request';
  return 'document';
}

function metaString(meta: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = meta[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function isOutboundCorrespondence(row: CorrespondenceRow) {
  const status = String(row.status || '').toLowerCase();
  const template = String(row.template_type || '');
  if (status === 'received') return false;
  if (template === 'customerResponseNotification') return false;
  return true;
}

function stampMs(value?: string | null) {
  if (!value) return NaN;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? NaN : ms;
}

function actionKind(row: CorrespondenceRow): TimelineKind | null {
  const meta = asMeta(row.metadata);
  const action = String(asAction(meta.customer_action || meta.customerAction).action || '').trim().toLowerCase();
  const subject = String(row.subject || '').toLowerCase();
  if (action === 'accept' || action === 'accepted' || subject.includes('customer accepted')) return 'accept';
  if (action === 'reject' || action === 'rejected' || subject.includes('customer rejected')) return 'reject';
  if (action === 'query' || action === 'queried' || subject.includes('customer query')) return 'query';
  return null;
}

function hasDocumentOpenTracking(row: CorrespondenceRow) {
  const template = String(row.template_type || '');
  if (
    template === 'sendEstimateQuote'
    || template === 'sendCustomerInvoice'
    || template === 'depositRequest'
    || template === 'outstandingPayment'
  ) {
    return true;
  }
  const meta = asMeta(row.metadata);
  return Boolean(metaString(meta, 'documentId', 'document_id', 'docId', 'shareLinkId'));
}

function sendOpenMeta(row: CorrespondenceRow) {
  const meta = asMeta(row.metadata);
  const openedAt = typeof meta.opened_at === 'string' ? meta.opened_at : undefined;
  const openCount = typeof meta.open_count === 'number' ? meta.open_count : (openedAt ? 1 : 0);
  const sentMs = stampMs(row.created_at);
  const openedMs = stampMs(openedAt);
  if (openedAt && Number.isFinite(sentMs) && Number.isFinite(openedMs) && openedMs + 60 * 1000 < sentMs) {
    return { openCount: 0, openedAt: undefined as string | undefined };
  }
  return { openCount, openedAt };
}

function documentIdFor(item: CorrespondenceRow, outbound: CorrespondenceRow[]) {
  const meta = asMeta(item.metadata);
  const direct = metaString(meta, 'documentId', 'document_id', 'docId');
  if (direct) return direct;
  const parentId = metaString(meta, 'parentEmailActivityId');
  if (parentId) {
    const parent = outbound.find((row) => row.id === parentId);
    const fromParent = parent ? metaString(asMeta(parent.metadata), 'documentId', 'document_id', 'docId') : '';
    if (fromParent) return fromParent;
  }
  const shareId = metaString(meta, 'shareLinkId');
  if (shareId) {
    const match = outbound.find((row) => metaString(asMeta(row.metadata), 'shareLinkId') === shareId);
    const fromShare = match ? metaString(asMeta(match.metadata), 'documentId', 'document_id', 'docId') : '';
    if (fromShare) return fromShare;
  }
  return '';
}

function relatedSend(item: CorrespondenceRow, outbound: CorrespondenceRow[]) {
  const itemMeta = asMeta(item.metadata);
  const parentId = metaString(itemMeta, 'parentEmailActivityId');
  const shareId = metaString(itemMeta, 'shareLinkId');
  const documentId = documentIdFor(item, outbound);
  const candidates = outbound.filter((row) => {
    const meta = asMeta(row.metadata);
    if (parentId && row.id === parentId) return true;
    if (shareId && metaString(meta, 'shareLinkId') === shareId) return true;
    if (documentId && metaString(meta, 'documentId', 'document_id', 'docId') === documentId) return true;
    return false;
  });
  return candidates.find((row) => (
    row.template_type === 'sendEstimateQuote' || row.template_type === 'sendCustomerInvoice'
  )) || candidates.find((row) => row.id === parentId) || candidates[0] || null;
}

function relatedSendLabel(item: CorrespondenceRow, outbound: CorrespondenceRow[]) {
  return relatedSend(item, outbound)?.subject || '';
}

function metaNumber(meta: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const raw = meta[key];
    const value = typeof raw === 'number' ? raw : Number(String(raw ?? '').trim());
    if (Number.isFinite(value) && value > 0) return Math.trunc(value);
  }
  return null;
}

function lifecycleRefFromRow(row: CorrespondenceRow) {
  const meta = asMeta(row.metadata);
  const fromMeta = metaString(meta, 'invoiceRef', 'lifecycleRef', 'lifecycle_ref', 'documentRef');
  if (fromMeta) return fromMeta;
  const subject = String(row.subject || '');
  const tagged = subject.match(/\((?:Invoice|Quote|Quotation|Estimate)\s+([A-Z]{2,}-\d+)\)/i);
  if (tagged?.[1]) return tagged[1];
  const bare = subject.match(/\b([A-Z]{2,}-\d{3,})\b/);
  return bare?.[1] || '';
}

function displaySubject(row: CorrespondenceRow) {
  const template = String(row.template_type || '');
  if (template === 'depositRequest') return 'Deposit Request';
  if (template === 'outstandingPayment') return 'Outstanding Payment';
  return row.subject || 'Untitled';
}

function actionTitle(kind: TimelineKind, noun: string) {
  if (kind === 'accept') return `Customer accepted this ${noun}`;
  if (kind === 'reject') return `Customer rejected this ${noun}`;
  return `Customer queried this ${noun}`;
}

const InitiationStagePanel: React.FC<InitiationStagePanelProps> = ({
  project,
  customer,
  tasks,
  onProjectPatch,
  onTaskChange,
  saving = false,
  calendarSyncKey = 0,
  onLinkedTaskChange,
  snapshot = null,
}) => {
  const [expandedStage, setExpandedStage] = useState<OperationalQueueId | null>(null);
  const [rows, setRows] = useState<CorrespondenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [preview, setPreview] = useState<EmailActivityLog | null>(null);
  const [calendarRefresh, setCalendarRefresh] = useState(0);
  const [createEventKey, setCreateEventKey] = useState(0);
  const [preferredEventType, setPreferredEventType] = useState<EventType | null>(null);

  const openCreateEvent = (eventType?: string) => {
    setPreferredEventType((eventType as EventType) || null);
    setCreateEventKey((value) => value + 1);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const { data, error: queryError } = await directSupabase
        .from('email_activity_log')
        .select('id, subject, recipient, status, template_type, created_at, metadata')
        .or(`metadata->>project_id.eq.${project.id},metadata->>projectId.eq.${project.id}`)
        .neq('template_type', 'windowOrder')
        .order('created_at', { ascending: false })
        .limit(80);

      if (cancelled) return;
      if (queryError) {
        setError(queryError.message);
        setRows([]);
        setLoading(false);
        return;
      }

      const all = (data || []) as CorrespondenceRow[];
      const outbound = all.filter(isOutboundCorrespondence);
      const shareIds = [...new Set(outbound.map((row) => metaString(asMeta(row.metadata), 'shareLinkId')).filter(Boolean))];
      const outboundIds = outbound.map((row) => row.id);
      const documentIds = [...new Set(outbound.map((row) => metaString(asMeta(row.metadata), 'documentId', 'document_id', 'docId')).filter(Boolean))];
      const orParts = [
        ...shareIds.map((id) => `metadata->>shareLinkId.eq.${id}`),
        ...outboundIds.map((id) => `metadata->>parentEmailActivityId.eq.${id}`),
        ...documentIds.map((id) => `metadata->>documentId.eq.${id}`),
      ];

      let related: CorrespondenceRow[] = all;
      if (orParts.length > 0) {
        const { data: extra } = await directSupabase
          .from('email_activity_log')
          .select('id, subject, recipient, status, template_type, created_at, metadata')
          .or(orParts.join(','))
          .limit(120);
        if (cancelled) return;
        const extraRows = (extra || []) as CorrespondenceRow[];
        const seen = new Set(related.map((row) => row.id));
        related = related.concat(extraRows.filter((row) => !seen.has(row.id)));
      }

      const docs = await documentsService.getDocumentsByProject(project.id).catch(() => []);
      if (cancelled) return;

      const versionByDocId = new Map<string, number>();
      const versionByLifecycle = new Map<string, number>();
      const versionByShareLinkId = new Map<string, number>();
      const docIds = docs.map((doc) => doc.id).filter(Boolean);
      if (shareIds.length > 0) {
        const { data: shareRows } = await directSupabase
          .from('document_share_links')
          .select('id, sent_version_number')
          .in('id', shareIds);
        for (const row of shareRows || []) {
          const shareId = String((row as { id?: string }).id || '');
          const versionNumber = Number((row as { sent_version_number?: number }).sent_version_number);
          if (!shareId || !Number.isFinite(versionNumber) || versionNumber <= 0) continue;
          versionByShareLinkId.set(shareId, Math.trunc(versionNumber));
        }
      }
      if (docIds.length > 0) {
        const { data: versionRows } = await directSupabase
          .from('document_versions')
          .select('document_id, version_number, is_active, is_transmitted')
          .in('document_id', docIds);
        const grouped = new Map<string, Array<{ version_number: number; is_active: boolean; is_transmitted: boolean }>>();
        for (const row of versionRows || []) {
          const documentId = String((row as { document_id?: string }).document_id || '');
          const versionNumber = Number((row as { version_number?: number }).version_number);
          if (!documentId || !Number.isFinite(versionNumber) || versionNumber <= 0) continue;
          const list = grouped.get(documentId) || [];
          list.push({
            version_number: Math.trunc(versionNumber),
            is_active: !!(row as { is_active?: boolean }).is_active,
            is_transmitted: !!(row as { is_transmitted?: boolean }).is_transmitted,
          });
          grouped.set(documentId, list);
        }
        for (const [documentId, list] of grouped) {
          const transmitted = list.filter((item) => item.is_transmitted).sort((a, b) => b.version_number - a.version_number)[0];
          const active = list.find((item) => item.is_active);
          const latest = list.slice().sort((a, b) => b.version_number - a.version_number)[0];
          const picked = transmitted || active || latest;
          if (picked) versionByDocId.set(documentId, picked.version_number);
        }
        for (const doc of docs) {
          const version = versionByDocId.get(doc.id);
          if (version == null || !doc.lifecycleRef) continue;
          const kind = String(doc.kind || '');
          const existing = versionByLifecycle.get(doc.lifecycleRef);
          if (existing == null || kind === 'Quotation' || kind === 'Estimate') {
            versionByLifecycle.set(doc.lifecycleRef, version);
          }
        }
      }

      const versionForRow = (row: CorrespondenceRow) => {
        const meta = asMeta(row.metadata);
        const fromMeta = metaNumber(meta, 'sentVersionNumber', 'sent_version_number', 'versionNumber', 'version_number');
        if (fromMeta) return fromMeta;
        const shareId = metaString(meta, 'shareLinkId');
        if (shareId && versionByShareLinkId.has(shareId)) return versionByShareLinkId.get(shareId) || null;
        const documentId = metaString(meta, 'documentId', 'document_id', 'docId');
        if (documentId && versionByDocId.has(documentId)) return versionByDocId.get(documentId) || null;
        const lifecycleRef = lifecycleRefFromRow(row);
        if (lifecycleRef && versionByLifecycle.has(lifecycleRef)) return versionByLifecycle.get(lifecycleRef) || null;
        return null;
      };

      const sends: CorrespondenceRow[] = outbound.map((row) => {
        const opens = sendOpenMeta(row);
        const version = versionForRow(row);
        return {
          ...row,
          kind: 'send',
          title: displaySubject(row),
          previewId: row.id,
          openCount: opens.openCount,
          openedAt: opens.openedAt,
          version,
        };
      });

      const actionSeen = new Set<string>();
      const actions: CorrespondenceRow[] = [];
      for (const item of related) {
        if (isOutboundCorrespondence(item)) continue;
        const kind = actionKind(item);
        if (!kind) continue;
        const meta = asMeta(item.metadata);
        const documentId = documentIdFor(item, sends);
        const dedupe = `${kind}:${documentId || item.id}`;
        if (actionSeen.has(dedupe)) continue;
        actionSeen.add(dedupe);
        const action = asAction(meta.customer_action || meta.customerAction);
        const linked = relatedSend(item, sends);
        actions.push({
          ...item,
          kind,
          subject: item.subject || actionTitle(kind, documentNoun(item)),
          created_at: action.at || (typeof meta.accepted_at === 'string' ? meta.accepted_at : item.created_at),
          previewId: item.id,
          relatedLabel: linked?.subject || relatedSendLabel(item, sends),
          detail: action.reason || action.message,
          version: versionForRow(item) ?? linked?.version ?? null,
        });
      }

      for (const doc of docs) {
        const status = String(doc.status || '').toLowerCase();
        const kind: TimelineKind | null = status === 'accepted' ? 'accept' : status === 'rejected' ? 'reject' : null;
        if (!kind) continue;
        const dedupe = `${kind}:${doc.id}`;
        if (actionSeen.has(dedupe)) continue;
        actionSeen.add(dedupe);
        const docKind = String(doc.kind || '').toLowerCase();
        const noun = docKind.includes('invoice') ? 'invoice' : docKind.includes('estimate') ? 'estimate' : 'quote';
        const at = kind === 'accept'
          ? (doc.acceptedAt || doc.statusChangedAt || '')
          : (doc.rejectedAt || doc.statusChangedAt || '');
        const linked = sends.find((row) => metaString(asMeta(row.metadata), 'documentId', 'document_id', 'docId') === doc.id);
        actions.push({
          id: `doc-${kind}-${doc.id}`,
          kind,
          subject: actionTitle(kind, noun),
          recipient: customer?.email || project.customer_email || '',
          status: kind === 'accept' ? 'accepted' : 'rejected',
          template_type: kind,
          created_at: at || linked?.created_at || doc.updatedAt,
          previewId: linked?.id || null,
          relatedLabel: [doc.lifecycleRef, linked?.subject].filter(Boolean).join(' · '),
          version: versionByDocId.get(doc.id) ?? linked?.version ?? null,
        });
      }

      setRows(
        [...sends, ...actions].sort((a, b) => (stampMs(b.created_at) || 0) - (stampMs(a.created_at) || 0))
      );
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [project.id, project.customer_email, customer?.email]);

  const openCorrespondence = async (id: string) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);
    const { data, error: loadError } = await emailActivity.getById(id);
    if (loadError || !data) {
      setPreviewError(loadError || 'Could not load this email');
      setPreviewLoading(false);
      return;
    }
    setPreview(data);
    setPreviewLoading(false);
  };

  const closeCorrespondence = () => {
    setPreviewOpen(false);
    setPreview(null);
    setPreviewError(null);
  };

  const previewAttachments = Array.isArray((preview?.metadata as { attachments?: Array<{ name?: string }> } | null)?.attachments)
    ? ((preview?.metadata as { attachments: Array<{ name?: string }> }).attachments)
    : [];

  return (
    <Stack gap={2}>
      <WorkspaceLifecycleRefCard project={project} onProjectPatch={onProjectPatch} />
      <ProjectLifecycleBar
        project={project}
        tasks={tasks}
        expandedStage={expandedStage}
        onExpandStage={setExpandedStage}
        onTaskChange={onTaskChange}
        eventsRefreshKey={calendarRefresh + calendarSyncKey}
        saving={saving}
        snapshot={snapshot}
      />
      <WorkspaceProductTypesCard project={project} onProjectPatch={onProjectPatch} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.4fr) minmax(280px, 0.6fr)' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Stack gap={2}>
          <WorkspaceCommentsCard project={project} onProjectPatch={onProjectPatch} />
          <JobCardCommentsCard projectId={project.id} />
          <WorkspaceCalendarEventsCard
            project={project}
            refreshKey={calendarRefresh + calendarSyncKey}
            onAddEvent={() => openCreateEvent()}
            onSchedule={(eventType) => openCreateEvent(eventType)}
            onLinkedTaskChange={onLinkedTaskChange}
          />
        </Stack>
        <WorkspaceRecentActivityCard project={project} />
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Typography level="title-md" sx={{ mb: 1.5 }}>Correspondence</Typography>
          <Divider sx={{ mb: 1.5 }} />
          {loading && <CircularProgress size="sm" />}
          {error && <Alert color="warning">Could not load project emails: {error}</Alert>}
          {!loading && !error && rows.length === 0 && (
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No emails tagged to this project yet.</Typography>
          )}
          <Stack gap={1}>
            {rows.map((row) => {
              const kind = row.kind || 'send';
              const kindColor = kind === 'accept' ? 'success' : kind === 'reject' ? 'danger' : kind === 'query' ? 'warning' : row.status === 'sent' ? 'success' : row.status === 'failed' ? 'danger' : 'neutral';
              const kindLabel = kind === 'send' ? row.status : kind === 'accept' ? 'Accepted' : kind === 'reject' ? 'Rejected' : 'Query';
              const showOpenTracking = kind === 'send' && hasDocumentOpenTracking(row);
              return (
              <Box
                key={row.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: showOpenTracking
                    ? { xs: '1fr', sm: 'minmax(0, 1.15fr) minmax(160px, 0.55fr)' }
                    : '1fr',
                  gap: { xs: 1, sm: 2 },
                  alignItems: 'start',
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography level="body-sm" fontWeight="lg">{row.title || row.subject || 'Untitled'}</Typography>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    {row.recipient} · {formatStamp(row.created_at)}
                  </Typography>
                  {row.detail && (
                    <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 0.25 }}>{row.detail}</Typography>
                  )}
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.75 }}>
                    {kind === 'send' && <Chip size="sm" variant="soft">{row.template_type}</Chip>}
                    {row.version != null && (
                      <Chip size="sm" variant="soft" color="primary">Version {row.version}</Chip>
                    )}
                    <Chip size="sm" variant="soft" color={kindColor}>
                      {kindLabel}
                    </Chip>
                    {row.previewId && (
                      <Button size="sm" variant="plain" onClick={() => { void openCorrespondence(row.previewId as string); }}>
                        Open
                      </Button>
                    )}
                  </Stack>
                </Box>
                {showOpenTracking && (
                <Box
                  sx={{
                    minWidth: 0,
                    pl: { sm: 2 },
                    borderLeft: { sm: '1px solid' },
                    borderColor: { sm: 'divider' },
                  }}
                >
                  <Typography level="body-xs" sx={{ color: row.openCount ? 'neutral.700' : 'neutral.400' }}>
                    {row.openCount
                      ? (row.openCount === 1 ? 'Opened' : `Opened ${row.openCount}×`)
                      : 'No views'}
                  </Typography>
                  {row.openedAt && !!row.openCount && (
                    <Typography level="body-xs" sx={{ color: 'neutral.400', mt: 0.25 }}>
                      {formatStamp(row.openedAt)}
                    </Typography>
                  )}
                </Box>
                )}
              </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {customer && (
        <EventManager
          compact
          modalOnly
          projectId={project.id}
          customer={customer}
          openCreateKey={createEventKey}
          preferredEventType={preferredEventType}
          onEventsChange={() => setCalendarRefresh((value) => value + 1)}
        />
      )}

      <Modal open={previewOpen} onClose={closeCorrespondence}>
        <ModalDialog
          size="lg"
          sx={{ maxWidth: { xs: '98vw', sm: 800 }, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
        >
          <ModalClose />
          <DialogTitle>Correspondence</DialogTitle>
          <DialogContent>
            {previewLoading && (
              <Stack alignItems="center" justifyContent="center" minHeight={200}>
                <CircularProgress />
              </Stack>
            )}
            {previewError && <Alert color="danger">{previewError}</Alert>}
            {!previewLoading && !previewError && preview && (
              <Stack spacing={1.5}>
                <Box>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Subject</Typography>
                  <Typography level="title-md">{preview.subject || 'Untitled'}</Typography>
                </Box>
                <Stack direction="row" gap={2} flexWrap="wrap">
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Recipient</Typography>
                    <Typography level="body-sm">{preview.recipient || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Status</Typography>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={preview.status === 'sent' ? 'success' : preview.status === 'failed' ? 'danger' : 'neutral'}
                    >
                      {preview.status}
                    </Chip>
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Sent</Typography>
                    <Typography level="body-sm">
                      {preview.created_at ? new Date(preview.created_at).toLocaleString('en-GB') : '—'}
                    </Typography>
                  </Box>
                </Stack>
                {preview.error_message && (
                  <Alert color="danger">{preview.error_message}</Alert>
                )}
                <Divider />
                <Sheet
                  variant="outlined"
                  sx={{ p: { xs: 1, sm: 2 }, borderRadius: 'sm', bgcolor: 'background.level1', overflowX: 'auto' }}
                >
                  <div
                    style={{ minWidth: 0, wordBreak: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: preview.content_html || '<p>No content</p>' }}
                  />
                </Sheet>
                {previewAttachments.length > 0 && (
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {previewAttachments.map((attachment, index) => (
                      <Chip key={`${attachment.name || 'file'}-${index}`} size="sm" variant="outlined">
                        {attachment.name || 'Attachment'}
                      </Chip>
                    ))}
                  </Stack>
                )}
              </Stack>
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Stack>
  );
};

export default InitiationStagePanel;
